/**
 * Quick Todo Widget — personal scratchpad for non-lead operational tasks.
 *
 * Mounted from the AdminShell sidebar. Click the "Quick notes" button → opens
 * a Sheet (slide-in panel from the right) with an inline-create list.
 *
 * Per-user, not shared. Backed by `/admin/personal-todos`.
 *
 * Sections:
 *   - Open todos (active, not snoozed, not done)
 *   - 💤 Snoozed (collapsible — items resurface tomorrow 8 AM IST automatically)
 *   - ✅ Done
 *
 * Per-item actions: check-off, snooze until tomorrow, delete.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Moon,
  NotebookPen,
  Plus,
  Sunrise,
  Trash2,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

function relativeAgo(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}

function formatSnoozeWake(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
}

export default function QuickTodoSheet({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snoozedCollapsed, setSnoozedCollapsed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/personal-todos");
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const { openItems, snoozedItems, doneItems } = useMemo(() => {
    const now = Date.now();
    const isSnoozed = (t) =>
      t.snoozed_until && new Date(t.snoozed_until).getTime() > now;
    return {
      openItems: items.filter((t) => !t.done && !isSnoozed(t)),
      snoozedItems: items.filter((t) => !t.done && isSnoozed(t)),
      doneItems: items.filter((t) => t.done),
    };
  }, [items]);

  const submitDraft = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/personal-todos", { text });
      setItems((prev) => [r.data, ...prev]);
      setDraft("");
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (todo) => {
    const next = !todo.done;
    setItems((prev) =>
      prev.map((p) =>
        p.id === todo.id
          ? { ...p, done: next, snoozed_until: next ? null : p.snoozed_until }
          : p,
      ),
    );
    try {
      await api.patch(`/admin/personal-todos/${todo.id}`, { done: next });
    } catch {
      load();
    }
  };

  const remove = async (todo) => {
    setItems((prev) => prev.filter((p) => p.id !== todo.id));
    try {
      await api.delete(`/admin/personal-todos/${todo.id}`);
    } catch {
      load();
    }
  };

  const snooze = async (todo) => {
    // Optimistic — set a placeholder snoozed_until in the future
    const placeholderWake = new Date(Date.now() + 12 * 3600 * 1000).toISOString();
    setItems((prev) =>
      prev.map((p) =>
        p.id === todo.id ? { ...p, snoozed_until: placeholderWake } : p,
      ),
    );
    try {
      const r = await api.post(`/admin/personal-todos/${todo.id}/snooze`);
      setItems((prev) => prev.map((p) => (p.id === todo.id ? r.data : p)));
    } catch {
      load();
    }
  };

  const unsnooze = async (todo) => {
    setItems((prev) =>
      prev.map((p) => (p.id === todo.id ? { ...p, snoozed_until: null } : p)),
    );
    try {
      const r = await api.post(`/admin/personal-todos/${todo.id}/unsnooze`);
      setItems((prev) => prev.map((p) => (p.id === todo.id ? r.data : p)));
    } catch {
      load();
    }
  };

  const clearCompleted = async () => {
    setItems((prev) => prev.filter((p) => !p.done));
    try {
      await api.post("/admin/personal-todos/clear-completed");
    } catch {
      load();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        data-testid="quick-todo-sheet"
      >
        <SheetHeader className="border-b border-slate-100 px-5 py-4">
          <SheetTitle className="flex items-center gap-2 text-[#0A2540]">
            <NotebookPen className="h-4 w-4 text-[#C9A961]" />
            Quick notes
          </SheetTitle>
          <p className="text-xs text-slate-500">
            Personal scratchpad for things that aren't tied to a lead — renewals,
            campaigns, ideas, follow-ups.
          </p>
        </SheetHeader>

        {/* Inline create */}
        <form
          onSubmit={submitDraft}
          className="border-b border-slate-100 px-5 py-3"
          data-testid="quick-todo-form"
        >
          <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#C9A961]/20">
            <Plus className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What's on your mind?  e.g. Renew SSL cert"
              className="flex-1 bg-transparent text-sm text-[#0A2540] outline-none placeholder:text-slate-400"
              autoFocus
              data-testid="quick-todo-input"
            />
            {draft && (
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-[#0A2540] px-2.5 py-1 text-[11px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                data-testid="quick-todo-save"
              >
                Save
              </button>
            )}
          </div>
          <div className="mt-1.5 px-1 text-[10px] text-slate-400">
            Press <kbd className="rounded bg-slate-100 px-1">Enter</kbd> to save · private to you
          </div>
        </form>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-5 py-10 text-center" data-testid="quick-todo-empty">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <NotebookPen className="h-5 w-5 text-[#C9A961]" />
              </div>
              <div className="mt-3 text-sm font-medium text-[#0A2540]">
                Nothing here yet
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Capture a quick reminder above. Only you can see this list.
              </div>
            </div>
          )}

          {/* Open todos */}
          {openItems.map((todo) => (
            <TodoRow
              key={todo.id}
              todo={todo}
              onToggle={toggleDone}
              onSnooze={snooze}
              onUnsnooze={unsnooze}
              onRemove={remove}
            />
          ))}

          {/* Snoozed section (collapsible) */}
          {snoozedItems.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={() => setSnoozedCollapsed((v) => !v)}
                className="flex w-full items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider text-slate-400 transition-colors hover:text-[#0A2540]"
                data-testid="quick-todo-snoozed-toggle"
              >
                {snoozedCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                <Moon className="h-3 w-3" />
                Snoozed · {snoozedItems.length}
              </button>
              {!snoozedCollapsed &&
                snoozedItems.map((todo) => (
                  <TodoRow
                    key={todo.id}
                    todo={todo}
                    snoozed
                    onToggle={toggleDone}
                    onSnooze={snooze}
                    onUnsnooze={unsnooze}
                    onRemove={remove}
                  />
                ))}
            </div>
          )}

          {/* Done section */}
          {doneItems.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-2">
              <div className="px-2.5 py-1 text-[11px] uppercase tracking-wider text-slate-400">
                Done · {doneItems.length}
              </div>
              {doneItems.map((todo) => (
                <TodoRow
                  key={todo.id}
                  todo={todo}
                  onToggle={toggleDone}
                  onSnooze={snooze}
                  onUnsnooze={unsnooze}
                  onRemove={remove}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-5 py-2.5 text-[11px] text-slate-500">
          <div>
            {openItems.length} open · {snoozedItems.length} snoozed · {doneItems.length} done
          </div>
          {doneItems.length > 0 && (
            <button
              type="button"
              onClick={clearCompleted}
              className="rounded px-1.5 py-0.5 transition-colors hover:bg-slate-200 hover:text-[#0A2540]"
              data-testid="quick-todo-clear-completed"
            >
              Clear completed
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TodoRow({ todo, snoozed, onToggle, onSnooze, onUnsnooze, onRemove }) {
  return (
    <div
      className={`group flex items-start gap-2 rounded-md px-2.5 py-2 transition-colors hover:bg-slate-50 ${todo.done || snoozed ? "opacity-60" : ""}`}
      data-testid={`quick-todo-row-${todo.id}`}
    >
      <button
        type="button"
        onClick={() => onToggle(todo)}
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors ${
          todo.done
            ? "border-emerald-500 bg-emerald-500 text-white"
            : "border-slate-300 bg-white hover:border-[#0A2540]"
        }`}
        data-testid={`quick-todo-check-${todo.id}`}
        aria-label={todo.done ? "Mark as not done" : "Mark as done"}
      >
        {todo.done && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      <div className="min-w-0 flex-1">
        <div
          className={`break-words text-sm text-[#0A2540] ${todo.done ? "line-through" : ""}`}
        >
          {todo.text}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-400">
          {snoozed ? (
            <>
              <Moon className="h-3 w-3" />
              <span>Wakes {formatSnoozeWake(todo.snoozed_until)}</span>
            </>
          ) : (
            <span>{relativeAgo(todo.created_at)}</span>
          )}
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="invisible mt-0.5 rounded text-slate-400 transition-colors hover:bg-slate-200 hover:text-[#0A2540] group-hover:visible"
            data-testid={`quick-todo-menu-${todo.id}`}
            title="More"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {!todo.done && !snoozed && (
            <DropdownMenuItem
              onClick={() => onSnooze(todo)}
              data-testid={`quick-todo-snooze-${todo.id}`}
            >
              <Moon className="mr-2 h-4 w-4" />
              Snooze until tomorrow
            </DropdownMenuItem>
          )}
          {snoozed && (
            <DropdownMenuItem
              onClick={() => onUnsnooze(todo)}
              data-testid={`quick-todo-wake-${todo.id}`}
            >
              <Sunrise className="mr-2 h-4 w-4" />
              Wake now
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => onRemove(todo)}
            className="text-rose-600 focus:text-rose-700"
            data-testid={`quick-todo-delete-${todo.id}`}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Sidebar trigger button — renders the "Quick notes" entry that opens the sheet. */
export function QuickTodoTrigger({ collapsed, openCount = 0, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="quick-todo-trigger"
      title={collapsed ? "Quick notes" : undefined}
      className={`mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-amber-50 hover:text-[#0A2540] ${
        collapsed ? "justify-center px-0" : ""
      }`}
    >
      <div className="relative shrink-0">
        <NotebookPen className="h-[18px] w-[18px] text-[#C9A961]" />
        {openCount > 0 && collapsed && (
          <span
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#C9A961] ring-2 ring-white"
            aria-label={`${openCount} open`}
          />
        )}
      </div>
      {!collapsed && (
        <span className="flex flex-1 items-center justify-between truncate">
          <span className="truncate">Quick notes</span>
          {openCount > 0 && (
            <span
              data-testid="quick-todo-count"
              className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#C9A961]/15 px-1.5 text-[10px] font-semibold text-[#8a7333]"
            >
              {openCount > 99 ? "99+" : openCount}
            </span>
          )}
        </span>
      )}
    </button>
  );
}
